import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { formatMoney } from '../../../../utils/numbers';

const OffersV4GuidanceTag = ({ label, variant }) => (
    <div className={`offers-v4-guidance-tag${variant ? ` offers-v4-guidance-tag--${variant}` : ''}`}>
        <span className="offers-v4-guidance-tag-text">{label}</span>
    </div>
);

OffersV4GuidanceTag.propTypes = {
    label: PropTypes.string.isRequired,
    variant: PropTypes.string,
};

const CheckIcon = () => (
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0.75 3.9101L3.42056 6.58066L9.25 0.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const TOOLTIP_CONTENT = {
    apr: 'APR (Annual Percentage Rate) represents the total annual cost of your loan, including your interest rate and origination fee. These numbers are estimates. Enrolling in autopay reduces your APR by 0.25%.',
    interest: 'This rate is built into your monthly payment amount and stays the same through the life of your loan.',
    originationFee: 'A one-time fee we\'ll deduct from your total loan amount once your loan is processed.',
    cashToYou: 'The cash you will receive after the origination fee is deducted.',
};

import { InfoIcon } from '../../icons';

class InfoTooltip extends React.Component {
    constructor(props) {
        super(props);
        this.state = { showTooltip: false };
        this.wrapperRef = React.createRef();
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
        this.isTouchDevice = 'ontouchstart' in window;
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside = (e) => {
        if (this.wrapperRef.current && !this.wrapperRef.current.contains(e.target)) {
            this.setState({ showTooltip: false });
        }
    };

    handleClick = () => {
        this.setState(prev => ({ showTooltip: !prev.showTooltip }));
    };

    handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            this.setState({ showTooltip: false });
        }
    };

    handleFocus = () => {
        this.setState({ showTooltip: true });
    };

    handleBlur = () => {
        this.setState({ showTooltip: false });
    };

    handleMouseEnter = () => {
        if (!this.isTouchDevice) {
            this.setState({ showTooltip: true });
        }
    };

    handleMouseLeave = () => {
        if (!this.isTouchDevice) {
            this.setState({ showTooltip: false });
        }
    };

    render() {
        const { tooltip, position, tooltipId } = this.props;
        const { showTooltip } = this.state;
        const tooltipClass = `offers-v4-offer-card__tooltip${position ? ` offers-v4-offer-card__tooltip--${position}` : ''}`;

        return (
            <span
                className="offers-v4-offer-card__info-icon-wrapper"
                ref={this.wrapperRef}
                tabIndex={0}
                aria-describedby={showTooltip ? tooltipId : undefined}
                onClick={this.handleClick}
                onKeyDown={this.handleKeyDown}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
            >
                <InfoIcon />
                {showTooltip && tooltip && (
                    <div id={tooltipId} className={tooltipClass} role="tooltip">
                        {tooltip}
                    </div>
                )}
            </span>
        );
    }
}

const OfferCardHeader = ({ monthlyPayment, duration, isSelected, onSelect }) => (
    <div className="offers-v4-offer-card__header">
        <div className="offers-v4-offer-card__monthly">
            <span className="offers-v4-offer-card__monthly-amount">{formatMoney(monthlyPayment)}</span>
            {' '}
            <span className="offers-v4-offer-card__monthly-label">Monthly</span>
        </div>
        <button
            id={`offers-v4-card-select-${duration}`}
            className={`offers-v4-offer-card__select-btn${isSelected ? ' offers-v4-offer-card__select-btn--selected' : ''}`}
            onClick={isSelected ? undefined : onSelect}
            aria-disabled={isSelected}
            type="button"
        >
            {isSelected && <CheckIcon />}
            {isSelected ? 'Selected' : 'Select Offer'}
        </button>
    </div>
);

const OfferCardMetrics = ({ annualPercentageRate, rate, duration }) => (
    <div className="offers-v4-offer-card__metrics">
        <div className="offers-v4-offer-card__metric">
            <div className="offers-v4-offer-card__metric-label-row">
                <span className="offers-v4-offer-card__metric-label">APR</span>
                <InfoTooltip tooltip={TOOLTIP_CONTENT.apr} tooltipId={`tooltip-apr-${duration}`} />
            </div>
            <span className="offers-v4-offer-card__metric-value">{annualPercentageRate.toFixed(2)}%</span>
        </div>
        <div className="offers-v4-offer-card__metric">
            <div className="offers-v4-offer-card__metric-label-row">
                <span className="offers-v4-offer-card__metric-label">Interest</span>
                <InfoTooltip tooltip={TOOLTIP_CONTENT.interest} tooltipId={`tooltip-interest-${duration}`} position="center" />
            </div>
            <span className="offers-v4-offer-card__metric-value">{rate.toFixed(2)}%</span>
        </div>
        <div className="offers-v4-offer-card__metric">
            <span className="offers-v4-offer-card__metric-label">Term</span>
            <span className="offers-v4-offer-card__metric-value">{duration} mo</span>
        </div>
    </div>
);

const OfferCardLineItem = ({ label, value, isLast, tooltip, tooltipId }) => (
    <div className={`offers-v4-offer-card__line-item${isLast ? ' offers-v4-offer-card__line-item--last' : ''}`}>
        <div className="offers-v4-offer-card__line-item-row">
            <div className="offers-v4-offer-card__line-item-content">
                <span className="offers-v4-offer-card__line-item-label">{label}</span>
                <InfoTooltip tooltip={tooltip} tooltipId={tooltipId} />
            </div>
            <span className="offers-v4-offer-card__line-item-value">{value}</span>
        </div>
    </div>
);

const OfferCardLineItems = ({ originationFee, amountFinanced, duration }) => (
    <div className="offers-v4-offer-card__line-items">
        <OfferCardLineItem label="Origination fee" value={`-${formatMoney(originationFee)}`} tooltip={TOOLTIP_CONTENT.originationFee} tooltipId={`tooltip-origination-fee-${duration}`} />
        <OfferCardLineItem label="Cash to you" value={formatMoney(amountFinanced)} tooltip={TOOLTIP_CONTENT.cashToYou} tooltipId={`tooltip-cash-to-you-${duration}`} isLast />
    </div>
);

class OffersV4OfferCard extends Component {
    static propTypes = {
        offer: PropTypes.shape({
            id: PropTypes.string.isRequired,
            amount: PropTypes.number.isRequired,
            duration: PropTypes.number.isRequired,
            rate: PropTypes.number.isRequired,
            annualPercentageRate: PropTypes.number.isRequired,
            monthlyPayment: PropTypes.number.isRequired,
            originationFee: PropTypes.number.isRequired,
            amountFinanced: PropTypes.number.isRequired,
        }).isRequired,
        onSelect: PropTypes.func.isRequired,
        guidanceTag: PropTypes.object,
        isSelected: PropTypes.bool,
    };

    static defaultProps = {
        guidanceTag: null,
        isSelected: false,
    };

    handleSelect = () => {
        this.props.onSelect(this.props.offer.id);
    };

    render() {
        const { offer, guidanceTag, isSelected } = this.props;
        const cardClass = `offers-v4-offer-card${isSelected ? ' offers-v4-offer-card--selected' : ''}`;

        return (
            <div className={cardClass}>
                {guidanceTag && <OffersV4GuidanceTag {...guidanceTag} />}
                <div className="offers-v4-offer-card__inner">
                    <div className="offers-v4-offer-card__content">
                        <OfferCardHeader
                            monthlyPayment={offer.monthlyPayment}
                            duration={offer.duration}
                            isSelected={isSelected}
                            onSelect={this.handleSelect}
                        />
                        <OfferCardMetrics
                            annualPercentageRate={offer.annualPercentageRate}
                            rate={offer.rate}
                            duration={offer.duration}
                        />
                        <OfferCardLineItems
                            originationFee={offer.originationFee}
                            amountFinanced={offer.amountFinanced}
                            duration={offer.duration}
                        />
                        
                    </div>
                </div>
            </div>
        );
    }
}

export default OffersV4OfferCard;
